import Phaser from 'phaser';
import { hasUrlFlag } from '../game/gameConfig';
import { sceneAfter } from '../game/scenes';
import { addHeroFramesToTexture, HERO_IDLE_FRAME } from '../game/heroFrames';
import { setImageHeight } from '../game/sceneArt';
import { attachKeyboard, createEmptyControls, type ControlState } from '../input/KeyboardAdapter';
import { createVirtualControls } from '../input/VirtualControls';
import { createIconHud } from '../ui/IconHud';
import { SynthSfx } from '../audio/SynthSfx';

const METEOR_KEYS = ['meteor_1', 'meteor_2', 'meteor_3'] as const;

export class MoonScene extends Phaser.Scene {
  private controls!: ControlState;
  private player!: Phaser.Types.Physics.Arcade.SpriteWithDynamicBody;
  private drillZone!: Phaser.GameObjects.Rectangle;
  private hint!: Phaser.GameObjects.Text;
  private meteors!: Phaser.Physics.Arcade.Group;
  private spawnClock = 0;
  private animTick = 0;
  private walkFrame = 0;
  private meteorHitCooldown = 0;
  private safeSpot = new Phaser.Math.Vector2(0, 0);
  private worldWidth = 2200;
  private sceneLocked = false;
  private freeze = false;

  constructor() {
    super('Moon');
  }

  preload() {
    this.load.image('hero_sheet', 'assets/hero-sheet-v3.png');
    this.load.image('rocket_pad', 'assets/rocket-pad-v3.png');
    this.load.image('meteor_1', 'assets/meteor-1.png');
    this.load.image('meteor_2', 'assets/meteor-2.png');
    this.load.image('meteor_3', 'assets/meteor-3.png');
    this.load.image('bg_moon', 'assets/bg-moon.png');
    this.load.image('drill_body', 'assets/drill-body.svg');
    this.load.image('drill_bit', 'assets/drill-bit.svg');
  }

  create() {
    createIconHud(this);
    this.freeze = hasUrlFlag('freeze');
    this.controls = createEmptyControls();
    attachKeyboard(this, this.controls);
    createVirtualControls(this, this.controls);

    this.physics.world.gravity.y = 260;
    this.worldWidth = Math.max(1600, Math.round(this.scale.width * 2));
    this.physics.world.setBounds(0, 0, this.worldWidth, this.scale.height + 120);
    this.cameras.main.setBounds(0, 0, this.worldWidth, this.scale.height);

    this.add.image(this.scale.width / 2, this.scale.height / 2, 'bg_moon')
      .setScrollFactor(0)
      .setDisplaySize(this.scale.width, this.scale.height)
      .setDepth(-30);

    addHeroFramesToTexture(this, 'hero_sheet');

    const groundY = this.scale.height - 44;
    const platformData = [
      { x: 200, y: groundY, width: 380, height: 40 },
      { x: 500, y: groundY - 64, width: 300, height: 36 },
      { x: 800, y: groundY - 128, width: 280, height: 34 },
      { x: 1080, y: groundY - 192, width: 270, height: 32 },
      { x: 1340, y: groundY - 256, width: 260, height: 30 },
    ];
    const platforms: Phaser.GameObjects.Rectangle[] = [];
    platformData.forEach((platform) => {
      const body = this.add.rectangle(platform.x, platform.y, platform.width, platform.height, 0x6d7482, 1).setDepth(6);
      this.physics.add.existing(body, true);
      platforms.push(body);
    });

    const floor = this.add.rectangle(this.worldWidth / 2, groundY + 54, this.worldWidth, 72, 0x000000, 0);
    this.physics.add.existing(floor, true);
    platforms.push(floor);

    const rocket = this.add.image(124, groundY - 130, 'rocket_pad').setDepth(9);
    setImageHeight(rocket, 208);

    this.player = this.physics.add.sprite(214, groundY - 156, 'hero_sheet', HERO_IDLE_FRAME);
    this.player.setDepth(12);
    this.player.setCollideWorldBounds(true);
    this.player.body.setSize(30, 128);
    this.player.body.setOffset(27, 91);
    this.safeSpot.set(this.player.x, this.player.y);

    this.meteors = this.physics.add.group();
    this.physics.add.collider(this.player, platforms);
    this.physics.add.overlap(this.player, this.meteors, (_player, meteorBody) => {
      const meteor = meteorBody as Phaser.Types.Physics.Arcade.SpriteWithDynamicBody;
      meteor.disableBody(true, true);
      if (this.meteorHitCooldown > 0) {
        return;
      }
      this.meteorHitCooldown = 450;
      this.player.setVelocity(Phaser.Math.Between(-110, 110), -180);
      this.hint.setText('Un meteoro te empujó. Sigue subiendo.');
    });

    this.drillZone = this.add.rectangle(1340, groundY - 312, 120, 120, 0x000000, 0);
    const drillBody = this.add.image(1340, groundY - 280, 'drill_body').setDepth(12);
    const drillBit = this.add.image(1340, groundY - 220, 'drill_bit').setDepth(12);
    setImageHeight(drillBody, 86);
    setImageHeight(drillBit, 42);
    this.add.text(1340, groundY - 362, 'TALADRO', {
      fontFamily: 'monospace',
      fontSize: '18px',
      color: '#ffe5a3',
    }).setOrigin(0.5).setDepth(12);

    this.hint = this.add.text(18, 18, 'Salta las plataformas y llega al taladro lunar.', {
      fontFamily: 'monospace',
      fontSize: '17px',
      color: '#edf7ff',
      wordWrap: { width: Math.min(430, this.scale.width - 36) },
    }).setScrollFactor(0).setDepth(2000);

    this.cameras.main.startFollow(this.player, true, 0.08, 0.08);
  }

  update(_time: number, delta: number) {
    if (this.sceneLocked) {
      this.player.setVelocity(0, 0);
      return;
    }

    const speed = 210;
    if (this.controls.left) {
      this.player.setVelocityX(-speed);
      this.player.setFlipX(true);
    } else if (this.controls.right) {
      this.player.setVelocityX(speed);
      this.player.setFlipX(false);
    } else {
      this.player.setVelocityX(0);
    }

    if (this.controls.jump && (this.player.body.blocked.down || this.player.body.touching.down)) {
      this.player.setVelocityY(-372);
      SynthSfx.jump();
    }

    if (this.player.body.blocked.down || this.player.body.touching.down) {
      this.safeSpot.set(this.player.x, this.player.y);
    }

    if (this.player.y > this.scale.height + 90) {
      this.player.setPosition(this.safeSpot.x, this.safeSpot.y - 20);
      this.player.setVelocity(0, 0);
      this.hint.setText('Vuelve a intentarlo desde la última plataforma.');
    }

    const nearDrill = Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), this.drillZone.getBounds());
    if (nearDrill) {
      this.hint.setText('Llegaste al taladro. Pulsa espacio para perforar la Luna.');
      if (this.controls.action) {
        this.startDrillScene();
        return;
      }
    }

    this.meteorHitCooldown = Math.max(0, this.meteorHitCooldown - delta);
    this.spawnClock += delta;
    if (!this.freeze && this.spawnClock >= 980) {
      this.spawnClock = 0;
      this.spawnMeteor();
    }

    this.meteors.getChildren().forEach((meteorObject) => {
      const meteor = meteorObject as Phaser.Types.Physics.Arcade.SpriteWithDynamicBody;
      if (meteor.active && meteor.y > this.scale.height + 140) {
        meteor.destroy();
      }
    });

    this.animTick += delta;
    if (
      this.animTick > 120 &&
      (this.controls.left || this.controls.right) &&
      (this.player.body.blocked.down || this.player.body.touching.down)
    ) {
      this.animTick = 0;
      this.walkFrame = (this.walkFrame + 1) % 5;
      this.player.setTexture('hero_sheet', `hero_${this.walkFrame}`);
    } else if (!this.controls.left && !this.controls.right) {
      this.player.setTexture('hero_sheet', HERO_IDLE_FRAME);
    }
  }

  private spawnMeteor() {
    const camera = this.cameras.main;
    const startX = Phaser.Math.Between(
      Math.round(camera.scrollX + 42),
      Math.round(Math.min(this.worldWidth - 42, camera.scrollX + camera.width - 42)),
    );
    const key = Phaser.Utils.Array.GetRandom([...METEOR_KEYS]);
    const meteor = this.meteors.create(startX, -54, key) as Phaser.Types.Physics.Arcade.SpriteWithDynamicBody;
    meteor.body.setAllowGravity(false);
    meteor.setScale(Phaser.Math.FloatBetween(0.72, 1.08));
    meteor.setVelocity(Phaser.Math.Between(-50, 50), Phaser.Math.Between(186, 246));
    meteor.setAngularVelocity(Phaser.Math.Between(-110, 110));
  }

  private startDrillScene() {
    this.sceneLocked = true;
    this.player.setVelocity(0, 0);
    this.hint.setText('Preparando el taladro...');
    this.time.delayedCall(500, () => {
      const next = sceneAfter('Moon');
      if (next) {
        this.scene.start(next);
      }
    });
  }
}
